import type { Feature, Position } from 'geojson';
import type { GeoboxDraw } from '../Types';
import { normalizeSketchFeatureToWgs84 } from './featureCreateGeometry';

/** Live cursor while drawing or hovering during edit. */
export type EditGeometryCursorPayload = {
  lng: number;
  lat: number;
  drawMode: string;
  /** Mouse position before snap (when available). */
  rawLng?: number;
  rawLat?: number;
  /** True when the effective placement point is magneted to a snap target. */
  snapLocked?: boolean;
};

/** One vertex of the sketch or edited feature. */
export type EditGeometryVertex = {
  lng: number;
  lat: number;
  vertexIndex: number;
  ringIndex?: number;
  /** True when this vertex is part of the current gl-draw selection. */
  selected?: boolean;
};

export type EditGeometryContext = 'idle' | 'drawing' | 'editing';

/** Draft or current geometry for host-rendered coordinate forms. */
export type EditGeometryPayload = {
  featureId: string | undefined;
  geometryType: string;
  vertices: EditGeometryVertex[];
  feature: Feature | null;
  drawMode: string;
  context: EditGeometryContext;
};

/**
 * Optional hooks so the host app can render its own coordinate form
 * (Vue/React) during feature draw, edit, or measurement.
 */
export type EditGeometryOptions = {
  onCursorMove?: (p: EditGeometryCursorPayload) => void;
  onGeometryChange?: (p: EditGeometryPayload) => void;
};

/** All gl-draw modes that produce or edit sketch geometry via the coordinate form. */
export const DRAW_SKETCH_MODES = [
  'draw_point',
  'draw_line_string',
  'draw_polygon',
  'draw_rectangle',
  'draw_circle',
  'draw_polygon_freehand',
  'draw_bezier',
  'passing_mode_point',
  'passing_mode_line_string',
  'passing_mode_polygon',
] as const;

const EDIT_SELECT_MODES = ['direct_select', 'simple_select'] as const;

export function isDrawSketchMode(mode: string): boolean {
  return (DRAW_SKETCH_MODES as readonly string[]).includes(mode);
}

export function isEditSelectMode(mode: string): boolean {
  return (EDIT_SELECT_MODES as readonly string[]).includes(mode);
}

/**
 * Normalize gl-draw `draw.selectionchange` payload points.
 * Entries may be GeoJSON positions, nested coordinate arrays, or {lng,lat} objects.
 */
export function positionsFromSelectionEvent(points: unknown): Position[] {
  if (!Array.isArray(points) || points.length === 0) return [];
  const out: Position[] = [];
  for (const item of points) {
    if (Array.isArray(item) && item.length >= 2) {
      const lng = Number(item[0]);
      const lat = Number(item[1]);
      if (Number.isFinite(lng) && Number.isFinite(lat)) {
        out.push([lng, lat]);
      }
      continue;
    }
    if (item && typeof item === 'object') {
      const rec = item as Record<string, unknown>;
      const lng = Number(rec.lng ?? rec.x ?? rec.longitude ?? rec.lon);
      const lat = Number(rec.lat ?? rec.y ?? rec.latitude);
      if (Number.isFinite(lng) && Number.isFinite(lat)) {
        out.push([lng, lat]);
      }
    }
  }
  return out;
}

export function emptyEditGeometryPayload(
  drawMode = '',
  context: EditGeometryContext = 'idle'
): EditGeometryPayload {
  return {
    featureId: undefined,
    geometryType: 'Empty',
    vertices: [],
    feature: null,
    drawMode,
    context,
  };
}

export type VerticesFromWgs84Options = {
  /** Omit gl-draw trailing cursor preview coordinate while sketching. */
  excludeTrailingPreview?: boolean;
};

/** Read vertices when geometry is already EPSG:4326 [longitude, latitude]. */
export function verticesFromWgs84Feature(
  feature: Feature,
  selectedPositions?: Position[],
  options?: VerticesFromWgs84Options
): EditGeometryVertex[] {
  const g = feature.geometry;
  if (!g || g.type === 'GeometryCollection') return [];

  const isValidPosition = (c: unknown): c is Position => {
    if (!Array.isArray(c)) return false;
    if (c.length < 2) return false;
    const lng = c[0];
    const lat = c[1];
    return Number.isFinite(lng) && Number.isFinite(lat);
  };

  const isSelected = (lng: number, lat: number) => {
    if (!selectedPositions?.length) return false;
    return selectedPositions.some((p) => {
      if (!isValidPosition(p)) return false;
      const plng = p[0];
      const plat = p[1];
      return Math.abs(plng - lng) < 1e-9 && Math.abs(plat - lat) < 1e-9;
    });
  };

  const vtx = (
    c: Position,
    vertexIndex: number,
    ringIndex?: number
  ): EditGeometryVertex => {
    const lng = c[0];
    const lat = c[1];
    return {
      lng,
      lat,
      vertexIndex,
      ringIndex,
      selected: isSelected(lng, lat),
    };
  };

  if (g.type === 'LineString') {
    let coords = g.coordinates;
    if (options?.excludeTrailingPreview) {
      // Before the first click, gl-draw keeps a single cursor-following preview point.
      coords = coords.length > 1 ? coords.slice(0, -1) : [];
    }
    return coords
      .filter((c) => isValidPosition(c))
      .map((c, vertexIndex) => vtx(c, vertexIndex));
  }
  if (g.type === 'Polygon') {
    const out: EditGeometryVertex[] = [];
    g.coordinates.forEach((ring, ringIndex) => {
      let coords = ring;
      if (options?.excludeTrailingPreview && ringIndex === 0) {
        coords = ring.length > 1 ? ring.slice(0, -1) : [];
      }
      coords
        .filter((c) => isValidPosition(c))
        .forEach((c, vertexIndex) => {
          out.push(vtx(c, vertexIndex, ringIndex));
        });
    });
    return out;
  }
  if (g.type === 'Point') {
    const c = g.coordinates;
    if (!isValidPosition(c)) return [];
    return [vtx(c, 0)];
  }
  if (g.type === 'MultiPoint') {
    return g.coordinates
      .filter((c) => isValidPosition(c))
      .map((c, vertexIndex) => vtx(c, vertexIndex));
  }
  if (g.type === 'MultiLineString') {
    const out: EditGeometryVertex[] = [];
    g.coordinates.forEach((line, lineIndex) => {
      line.forEach((c, vertexIndex) => {
        if (!isValidPosition(c)) return;
        out.push({ ...vtx(c, vertexIndex), ringIndex: lineIndex });
      });
    });
    return out;
  }
  if (g.type === 'MultiPolygon') {
    const out: EditGeometryVertex[] = [];
    g.coordinates.forEach((poly, polyIndex) => {
      const ring = poly[0] || [];
      ring.forEach((c, vertexIndex) => {
        if (!isValidPosition(c)) return;
        out.push({ ...vtx(c, vertexIndex), ringIndex: polyIndex });
      });
    });
    return out;
  }
  return [];
}

/** @deprecated Prefer {@link verticesFromWgs84Feature} on normalized geometry. */
export function verticesFromFeature(
  feature: Feature,
  selectedPositions?: Position[]
): EditGeometryVertex[] {
  const g = feature.geometry;
  if (!g || g.type === 'GeometryCollection') return [];
  const wgs84 = normalizeSketchFeatureToWgs84(feature);
  return verticesFromWgs84Feature(wgs84, selectedPositions);
}

export function payloadFromFeature(
  feature: Feature | null | undefined,
  drawMode: string,
  context: EditGeometryContext,
  selectedPositions?: Position[]
): EditGeometryPayload {
  if (!feature?.geometry) {
    return emptyEditGeometryPayload(drawMode, context);
  }
  const wgs84 = normalizeSketchFeatureToWgs84(feature);
  const geomType = wgs84.geometry!.type;
  const excludeTrailingPreview =
    context === 'drawing' &&
    (geomType === 'Polygon' || geomType === 'LineString');
  return {
    featureId:
      feature.id !== undefined && feature.id !== null
        ? String(feature.id)
        : undefined,
    geometryType: geomType,
    vertices: verticesFromWgs84Feature(wgs84, selectedPositions, {
      excludeTrailingPreview,
    }),
    feature: wgs84,
    drawMode,
    context,
  };
}

type DirectSelectDrawTools = GeoboxDraw & {
  _ctx?: {
    events?: {
      currentMode?: {
        state?: { featureId?: string | number; feature?: Feature };
      };
    };
  };
  get?: (id: string) => { toGeoJSON?: () => Feature } | Feature;
};

/** Feature under edit in gl-draw direct_select (getSelected is often empty there). */
function resolveDirectSelectFeature(drawTools: GeoboxDraw): Feature | null {
  try {
    const tools = drawTools as DirectSelectDrawTools;
    const currentMode = tools._ctx?.events?.currentMode as Record<string, unknown> | undefined;
    const rawState =
      currentMode && typeof currentMode.state === 'object' && currentMode.state != null
        ? currentMode.state
        : currentMode;
    const state = rawState as { featureId?: string | number; feature?: Feature } | undefined;
    const featureId = state?.featureId ?? state?.feature?.id;
    if (featureId == null) return null;

    const raw = tools.get?.(String(featureId));
    if (!raw) return null;

    if (typeof raw === 'object' && raw !== null && 'toGeoJSON' in raw) {
      const toGeoJSON = (raw as { toGeoJSON?: () => Feature }).toGeoJSON;
      if (typeof toGeoJSON === 'function') {
        return toGeoJSON();
      }
    }
    return raw as Feature;
  } catch {
    return null;
  }
}

/** Resolve the feature currently being drawn or edited in gl-draw. */
export function resolveActiveDrawFeature(drawTools: GeoboxDraw): {
  feature: Feature | null;
  context: EditGeometryContext;
} {
  const mode = String(drawTools.getMode());

  if (isDrawSketchMode(mode)) {
    const data = drawTools.getAll();
    if (!data.features?.length) {
      return { feature: null, context: 'drawing' };
    }
    return {
      feature: data.features[data.features.length - 1] as Feature,
      context: 'drawing',
    };
  }

  if (mode === 'direct_select') {
    const direct = resolveDirectSelectFeature(drawTools);
    if (direct) {
      return { feature: direct, context: 'editing' };
    }
  }

  if (isEditSelectMode(mode) || mode === 'scaleRotateMode') {
    const selected = drawTools.getSelected();
    if (selected?.features?.length) {
      return {
        feature: selected.features[0] as Feature,
        context: 'editing',
      };
    }
    const all = drawTools.getAll();
    if (all.features?.length === 1) {
      return { feature: all.features[0] as Feature, context: 'editing' };
    }
    if (all.features?.length) {
      return { feature: all.features[0] as Feature, context: 'editing' };
    }
  }

  return { feature: null, context: 'idle' };
}

export function buildEditGeometryPayload(
  drawTools: GeoboxDraw,
  selectedPositions?: Position[]
): EditGeometryPayload {
  const mode = String(drawTools.getMode());
  const { feature, context } = resolveActiveDrawFeature(drawTools);
  if (!feature) {
    return emptyEditGeometryPayload(mode, context);
  }
  return payloadFromFeature(feature, mode, context, selectedPositions);
}

export function applyVertexLngLatToFeature(
  feature: Feature,
  vertexIndex: number,
  lng: number,
  lat: number,
  opts?: { polygonRingIndex?: number }
): Feature | null {
  const updated = JSON.parse(JSON.stringify(feature)) as Feature;
  const g = updated.geometry;
  if (!g) return null;

  if (g.type === 'LineString') {
    const coords = [...g.coordinates] as Position[];
    if (vertexIndex < 0 || vertexIndex >= coords.length) return null;
    coords[vertexIndex] = [lng, lat];
    g.coordinates = coords;
  } else if (g.type === 'Polygon') {
    const ringIndex = opts?.polygonRingIndex ?? 0;
    const rings = g.coordinates.map((ring: Position[]) =>
      ring.map((p) => [...p] as Position)
    );
    const ring = rings[ringIndex];
    if (!ring || vertexIndex < 0 || vertexIndex >= ring.length) return null;
    ring[vertexIndex] = [lng, lat];
    const first = ring[0];
    const last = ring[ring.length - 1];
    const closed =
      ring.length > 1 && first[0] === last[0] && first[1] === last[1];
    if (closed) {
      if (vertexIndex === 0) ring[ring.length - 1] = [lng, lat];
      else if (vertexIndex === ring.length - 1) ring[0] = [lng, lat];
    }
    rings[ringIndex] = ring;
    g.coordinates = rings;
  } else if (g.type === 'Point') {
    if (vertexIndex !== 0) return null;
    g.coordinates = [lng, lat];
  } else if (g.type === 'MultiPoint') {
    if (vertexIndex < 0 || vertexIndex >= g.coordinates.length) return null;
    g.coordinates[vertexIndex] = [lng, lat];
  } else if (g.type === 'MultiLineString') {
    const lineIndex = opts?.polygonRingIndex ?? 0;
    const line = g.coordinates[lineIndex];
    if (!line || vertexIndex < 0 || vertexIndex >= line.length) return null;
    line[vertexIndex] = [lng, lat];
  } else if (g.type === 'MultiPolygon') {
    const polyIndex = opts?.polygonRingIndex ?? 0;
    const poly = g.coordinates[polyIndex];
    if (!poly?.[0]) return null;
    const ring = poly[0];
    if (vertexIndex < 0 || vertexIndex >= ring.length) return null;
    ring[vertexIndex] = [lng, lat];
    const first = ring[0];
    const last = ring[ring.length - 1];
    const closed =
      ring.length > 1 && first[0] === last[0] && first[1] === last[1];
    if (closed) {
      if (vertexIndex === 0) ring[ring.length - 1] = [lng, lat];
      else if (vertexIndex === ring.length - 1) ring[0] = [lng, lat];
    }
  } else {
    return null;
  }

  return updated;
}

/** Apply WGS84 lng/lat to a gl-draw feature (normalizes storage CRS first). */
export function applyWgs84VertexToDrawFeature(
  rawFeature: Feature,
  vertexIndex: number,
  lng: number,
  lat: number,
  opts?: { polygonRingIndex?: number }
): Feature | null {
  const wgs84 = normalizeSketchFeatureToWgs84(rawFeature);
  const updatedWgs84 = applyVertexLngLatToFeature(
    wgs84,
    vertexIndex,
    lng,
    lat,
    opts
  );
  if (!updatedWgs84) return null;
  return {
    ...updatedWgs84,
    id: rawFeature.id,
    properties: rawFeature.properties ?? updatedWgs84.properties ?? {},
  };
}

function isClosedRing(ring: Position[]): boolean {
  if (ring.length < 2) return false;
  const first = ring[0];
  const last = ring[ring.length - 1];
  return first[0] === last[0] && first[1] === last[1];
}

function removeVertexFromRing(
  ring: Position[],
  vertexIndex: number
): Position[] | null {
  if (vertexIndex < 0 || vertexIndex >= ring.length) return null;

  const closed = isClosedRing(ring);
  const minLength = closed ? 4 : 2;
  if (ring.length <= minLength) return null;

  const removeIndex =
    closed && vertexIndex === ring.length - 1 ? 0 : vertexIndex;
  const next = ring.filter((_, i) => i !== removeIndex);

  if (closed && next.length > 0) {
    const first = next[0];
    next[next.length - 1] = [first[0], first[1]];
  }

  return next;
}

/** Remove one vertex from a feature geometry. Returns null when removal is not allowed. */
export function removeVertexFromFeature(
  feature: Feature,
  vertexIndex: number,
  opts?: { polygonRingIndex?: number }
): Feature | null {
  const updated = JSON.parse(JSON.stringify(feature)) as Feature;
  const g = updated.geometry;
  if (!g) return null;

  if (g.type === 'LineString') {
    const coords = [...g.coordinates] as Position[];
    if (coords.length <= 2) return null;
    if (vertexIndex < 0 || vertexIndex >= coords.length) return null;
    coords.splice(vertexIndex, 1);
    g.coordinates = coords;
  } else if (g.type === 'Polygon') {
    const ringIndex = opts?.polygonRingIndex ?? 0;
    const rings = g.coordinates.map((ring: Position[]) => [...ring]);
    const ring = rings[ringIndex];
    if (!ring) return null;
    const nextRing = removeVertexFromRing(ring, vertexIndex);
    if (!nextRing) return null;
    rings[ringIndex] = nextRing;
    g.coordinates = rings;
  } else if (g.type === 'Point') {
    return null;
  } else if (g.type === 'MultiPoint') {
    if (g.coordinates.length <= 1) return null;
    if (vertexIndex < 0 || vertexIndex >= g.coordinates.length) return null;
    g.coordinates = g.coordinates.filter((_, i) => i !== vertexIndex);
  } else if (g.type === 'MultiLineString') {
    const lineIndex = opts?.polygonRingIndex ?? 0;
    const lines = g.coordinates.map((line: Position[]) => [...line]);
    const line = lines[lineIndex];
    if (!line || line.length <= 2) return null;
    if (vertexIndex < 0 || vertexIndex >= line.length) return null;
    lines[lineIndex] = line.filter((_, i) => i !== vertexIndex);
    g.coordinates = lines;
  } else if (g.type === 'MultiPolygon') {
    const polyIndex = opts?.polygonRingIndex ?? 0;
    const polys = g.coordinates.map((poly: Position[][]) =>
      poly.map((ring) => [...ring])
    );
    const poly = polys[polyIndex];
    if (!poly?.[0]) return null;
    const nextRing = removeVertexFromRing(poly[0], vertexIndex);
    if (!nextRing) return null;
    poly[0] = nextRing;
    polys[polyIndex] = poly;
    g.coordinates = polys;
  } else {
    return null;
  }

  return updated;
}

/** Remove one WGS84 vertex from a gl-draw feature (normalizes storage CRS first). */
export function removeWgs84VertexFromDrawFeature(
  rawFeature: Feature,
  vertexIndex: number,
  opts?: { polygonRingIndex?: number }
): Feature | null {
  const wgs84 = normalizeSketchFeatureToWgs84(rawFeature);
  const updatedWgs84 = removeVertexFromFeature(wgs84, vertexIndex, opts);
  if (!updatedWgs84) return null;
  return {
    ...updatedWgs84,
    id: rawFeature.id,
    properties: rawFeature.properties ?? updatedWgs84.properties ?? {},
  };
}
